import styled from '@emotion/styled'
import { Gray600, Gray900, Primary500 } from '@mehilainen/mds-customer/colors'
import { AngleRight, ExternalLink, Redo } from '@mehilainen/mds-customer/icons'
import { CircularProgress } from '@mui/material'
import { useAtomValue } from 'jotai'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationStyle, SupportedLanguage } from '../../../../__generated__/api'
import { DefaultButton } from '../../../../common/components/DefaultButton/DefaultButton'
import {
  CenteredColumnFlex,
  CenteredRowFlex,
  ColumnFlex,
  MinMaxDiv,
} from '../../../../common/components/Layout/Layout'
import Notification from '../../../../common/components/Notification/Notification'
import { Text } from '../../../../common/components/Typography/Typography'
import { useApi } from '../../../../common/hooks/useApi'
import { useInsuranceOptions } from '../../../../common/hooks/useInsurance'
import api from '../../../../common/services/api'
import { scale } from '../../../../common/utils/scale'
import {
  isFromAppAtom,
  isRedirectedFromInsuranceProviderAtom,
} from '../../../../state/common/atoms'
import useReserveState from '../../hooks/useReserveState'
import LahiTapiolaSVG from '../../images/LahiTapiola.svg?react'
import LahiTapiolaCheckSVG from '../../images/LahiTapiolaCheck.svg?react'
import { DirectCompensationInformation } from '../../utils/insurance'
import { serializeQueryData } from '../InsuranceRedirect'

import CustomershipCheck from './CustomershipCheck'
import {
  ButtonContent,
  Container,
  ContentContainer,
  Heading,
  LoaderWrapper,
  Paragraph,
  StyledButton,
} from './Shared'

interface DirectCompensationButtonProps {
  onClick(): void
  title: string
  subtitle: string
  subtitle2: string
  subtitle3: string
}

export const DirectCompensationButton = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<DirectCompensationButtonProps>
>(({ title, subtitle, subtitle2, subtitle3, onClick }, ref) => {
  return (
    <StyledButton ref={ref} onClick={onClick} variant="outlined">
      <ButtonContent>
        <CenteredRowFlex $gap="16px">
          <LahiTapiolaCheckSVG />
          <ColumnFlex>
            <Text $size={400} $weight="Regular" $height="Medium" $color={Gray900}>
              {title}
            </Text>
            <Text $size={200} $weight="Regular" $height="Large" $color={Gray900}>
              {subtitle}
            </Text>
            <Text $size={200} $weight="Regular" $height="Large" $color={Gray600}>
              {subtitle2}
            </Text>
            <Text $size={200} $weight="Regular" $height="Large" $color={Gray600}>
              {subtitle3}
            </Text>
          </ColumnFlex>
        </CenteredRowFlex>
        <AngleRight htmlColor={Primary500} fontSize="large" />
      </ButtonContent>
    </StyledButton>
  )
})

interface ContinueButtonProps {
  onClick(): void
  pending: boolean
}

export const ContinueButton = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<ContinueButtonProps>
>(({ onClick, pending }, ref) => {
  const { t } = useTranslation()
  return (
    <StyledButton
      ref={ref}
      onClick={onClick}
      variant="outlined"
      aria-label={
        pending
          ? t('common.loadingText')
          : t('component.lahiTapiolaForm.directCompensationSelect.continueAsSelfPaying')
      }
    >
      <ButtonContent>
        {pending ? (
          <LoaderWrapper>
            <CircularProgress />
          </LoaderWrapper>
        ) : (
          <Text $size={400} $height="Medium" $weight="Regular" $color={Gray900}>
            {t('component.lahiTapiolaForm.directCompensationSelect.continueAsSelfPaying')}
          </Text>
        )}
        <AngleRight htmlColor={Primary500} fontSize="large" />
      </ButtonContent>
    </StyledButton>
  )
})

interface SeekCompensationPermitsButtonProps {
  // TODO: props required for redirect
}

export const SeekCompensationPermitsButton: React.FC<
  React.PropsWithChildren<SeekCompensationPermitsButtonProps>
> = ({}) => {
  const { t, i18n } = useTranslation()
  const isFromApp = useAtomValue(isFromAppAtom)

  const {
    selectedUser,
    loggedInUser,
    appointmentId,
    selectedAppointmentType,
    selectedAppointmentLength,
    selectedInsuranceContractId,
    selectedInsurancePayerId,
    selectedNodeId,
    selectedServiceId,
  } = useReserveState()

  if (!selectedInsuranceContractId) {
    throw `Could not form link for seeking insurance compensation permit: no insurance contract id selected.`
  }

  if (!selectedInsurancePayerId) {
    throw `Could not form link for seeking insurance compensation permit: no insurance payer id selected.`
  }

  const data = serializeQueryData({
    userOmUid: selectedUser.omUid,
    appointmentId,
    appointmentType: selectedAppointmentType,
    appointmentLength: selectedAppointmentLength,
    language: i18n.language as SupportedLanguage,
    insuranceContractId: selectedInsuranceContractId,
    insurancePayerId: selectedInsurancePayerId,
    ...(selectedNodeId ? { nodeId: selectedNodeId } : {}),
    ...(selectedServiceId ? { serviceId: selectedServiceId } : {}),
    isFromApp,
  })

  return (
    <StyledButton
      href={`${process.env.REACT_APP_INSURANCES_API_URL}/v1/direct-compensation-permits/lähitapiola?channel=web&lang=${i18n.language}&subjectOmUid=${selectedUser.omUid}&authorizedOmUid=${loggedInUser?.omUid}&data=${data}&system=av`}
      variant="outlined"
      className="no-link-styles no-external-icon"
    >
      <ButtonContent>
        <Text $size={400} $height="Medium" $weight="Regular" $color={Gray900}>
          {t('component.lahiTapiolaForm.directCompensationSelect.seekCompensationPermits')}
        </Text>
        <ExternalLink htmlColor={Primary500} fontSize="large" />
      </ButtonContent>
    </StyledButton>
  )
}

export const ButtonGroupContainer = styled(CenteredColumnFlex)`
  width: 100%;
  gap: ${scale(1)};
`

const RefreshButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${scale(1)};
`

interface DirectCompensationSelectProps {
  subjectOmUid: number
  onSelect(id: string): void
  onBack(): void
  onContinueAsSelfPaying(): void
}

const DirectCompensationSelect: React.FC<
  React.PropsWithChildren<DirectCompensationSelectProps>
> = ({ subjectOmUid, onSelect, onBack, onContinueAsSelfPaying }) => {
  const { t, i18n } = useTranslation()
  const [refetchKey, setRefetchKey] = useState(0)

  const {
    data: directCompensationOptions,
    pending,
    error,
  } = useApi(
    api.v1.insuranceDirectCompensationPermits,
    {
      subjectOmUid,
      insuranceCompanyId: 'lähitapiola',
      lang: i18n.language as SupportedLanguage,
      _refetchKey: refetchKey, // Force refetch when key changes
    } as Parameters<typeof api.v1.insuranceDirectCompensationPermits>[0],
    []
  )

  const handleRefresh = useCallback(() => {
    setRefetchKey((prev) => prev + 1)
  }, [])

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    buttonRef.current?.focus()
  })

  if (error) {
    throw 'Error loading direct compensation permits'
  }

  return (
    <ContentContainer>
      <Heading>{t('component.lahiTapiolaForm.directCompensationSelect.heading')}</Heading>
      <Paragraph>{t('component.lahiTapiolaForm.directCompensationSelect.text')}</Paragraph>
      <MinMaxDiv size="540px">
        <ButtonGroupContainer>
          {directCompensationOptions.map((option, i) => (
            <DirectCompensationButton
              key={`diractCompensationButton-${option.id}-${i}`}
              onClick={() => onSelect(option.id)}
              title={option.i18n.title?.[i18n.language as SupportedLanguage] ?? ''}
              subtitle={option.i18n.subtitle?.[i18n.language as SupportedLanguage] ?? ''}
              subtitle2={option.i18n.subtitle2?.[i18n.language as SupportedLanguage] ?? ''}
              subtitle3={
                (option.i18n as { subtitle3?: { fi: string; en: string; sv: string } }).subtitle3?.[
                  i18n.language as SupportedLanguage
                ] ?? ''
              }
              ref={i === 0 && !pending ? buttonRef : undefined}
            />
          ))}
          <RefreshButtonContainer>
            <DefaultButton
              variant="text"
              onClick={pending ? undefined : handleRefresh}
              startIcon={pending ? <CircularProgress size={20} /> : <Redo />}
              ref={pending && refetchKey > 0 ? buttonRef : undefined}
              aria-label={
                pending
                  ? t('common.loadingText')
                  : t('component.lahiTapiolaForm.directCompensationSelect.refresh')
              }
            >
              {t('component.lahiTapiolaForm.directCompensationSelect.refresh')}
            </DefaultButton>
          </RefreshButtonContainer>
          {!pending && directCompensationOptions.length === 0 && (
            <Notification
              style={NotificationStyle.Info}
              title={t('component.lahiTapiolaForm.directCompensationSelect.note.heading')}
            >
              {t('component.lahiTapiolaForm.directCompensationSelect.note.text')}
            </Notification>
          )}
          <ContinueButton
            onClick={onContinueAsSelfPaying}
            pending={pending}
            ref={pending && refetchKey === 0 ? buttonRef : undefined}
          />
          <SeekCompensationPermitsButton />
        </ButtonGroupContainer>
      </MinMaxDiv>
      <DefaultButton variant="text" onClick={onBack}>
        {t('common.goBack')}
      </DefaultButton>
    </ContentContainer>
  )
}

interface Props {
  onSubmit(data?: DirectCompensationInformation): void
  onBack(): void
  onContinueAsSelfPaying(): void
  onContinueAsSelfPayingVerified(): void
}

const LahiTapiolaForm: React.FC<React.PropsWithChildren<Props>> = ({
  onSubmit,
  onBack,
  onContinueAsSelfPaying,
  onContinueAsSelfPayingVerified,
}) => {
  const { selectedUser } = useReserveState()
  const isRedirectedFromInsuranceProvider = useAtomValue(isRedirectedFromInsuranceProviderAtom)
  const [customershipVerified, setCustomershipVerified] = useState<boolean>(false)

  const {
    insuranceOptions: options,
    pending: optionsPending,
    error: optionsError,
  } = useInsuranceOptions(selectedUser.omUid, 'lähitapiola')

  const onContinue = customershipVerified ? onContinueAsSelfPayingVerified : onContinueAsSelfPaying

  if (optionsPending) {
    return null
  }

  if (optionsError) {
    throw 'Error fetching insurance options'
  }

  if (
    isRedirectedFromInsuranceProvider ||
    customershipVerified ||
    options?.checkCustomership === false
  ) {
    return (
      <Container>
        <DirectCompensationSelect
          subjectOmUid={selectedUser.omUid}
          onSelect={(id) => onSubmit({ permitId: id })}
          onBack={onBack}
          onContinueAsSelfPaying={onContinue}
        />
      </Container>
    )
  }

  return (
    <Container>
      <CustomershipCheck
        insurancesApiInsuranceCompanyId="lähitapiola"
        translationKey="lahiTapiolaForm"
        Logo={LahiTapiolaSVG}
        subjectOmUid={selectedUser.omUid}
        onBack={onBack}
        onSuccess={() => setCustomershipVerified(true)}
        onContinueAsSelfPaying={onContinue}
      />
    </Container>
  )
}

export default LahiTapiolaForm
